import React, { FunctionComponent, useEffect, useRef, useState } from 'react'

import axios from 'axios'
import { Stack } from 'react-bootstrap'
import type { UploadedBankStmtRes, Banks } from '../types/upload-file-response'
import { ArrowClockwise } from 'react-bootstrap-icons'
import dayjs from 'dayjs'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import {
  getIntegrationURLFromENV,
  getIntegrationURLFromENVForPLD,
} from '../../../utils/getIntegrationURLFromENV'
import { AlexiApplicationTypes } from '../../../utils/constants/AlexiApplicationType.enum'
import { sendErrorLog } from '../utils/perfiosTransaction'
import { ErrorLogUploadType } from '../utils/constants/ErrorLogUploadType.enum'
import { Xicon } from '../../../svg/Xicon'

interface UploadBankStatementProps {
  file: File & { key: string }
  validateMonthsTill?: number
  onClose: () => void
  handleUploadedFileRes: (_: UploadedBankStmtRes) => void
  refId?: string
  refType?: string
  password?: string
  error?: string
  handleFailedFiles: (file: any, status: any, error: any, res: UploadedBankStmtRes | null) => void
  getUploadStatus: (status: 'error' | 'loading' | 'success') => void
  customYearMonthFrom?: string // YYYY-MM format
  customYearMonthTo?: string // YYYY-MM format
}

const UploadBankStatement: FunctionComponent<UploadBankStatementProps> = ({
  file,
  onClose,
  handleUploadedFileRes,
  handleFailedFiles,
  refId,
  refType,
  password,
  error,
  validateMonthsTill = 6,
  getUploadStatus,
  customYearMonthFrom,
  customYearMonthTo,
}) => {
  const [uploadStatus, setUploadStatus] = useState<'error' | 'loading' | 'success'>(
    error ? 'error' : 'loading'
  )
  const [errorMsg, setErrorMsg] = useState(error)
  const [skipDetails, setSkipDetails] = useState(false)
  const shouldRun = useRef(true)
  useEffect(() => {
    // setUploadStatus(error ? "error" : "loading");

    if (shouldRun.current) {
      shouldRun.current = false
      if (Object.keys(file).length) {
        setTimeout(async () => {
          await uploadFile(file)
        }, 1000)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])

  const uploadFile = async (files: File) => {
    if (error) return

    const token = window.localStorage.getItem('perfiosId') as string
    const transactionId = JSON.parse(token)
    let fileUploadResponse = null

    let formData = new FormData()
    formData.append('file', files)
    formData.append('refId', `${refId}`)
    formData.append('validateMonthsTill', `${validateMonthsTill}`)
    formData.append(
      's3Path',
      `${refId}/${dayjs().format('DD-MM-YYYY')}/${files.name.replace(
        /(?:\.(?![^.]+$)|[^\w.])+/g,
        '_'
      )}`
    )
    formData.append('perfiosTransactionId', transactionId?.perfiosTransactionId)
    if (password) {
      formData.append('password', password)
    }

    // Add custom date range if provided
    if (customYearMonthFrom && customYearMonthTo) {
      formData.append('customYearMonthFrom', customYearMonthFrom)
      formData.append('customYearMonthTo', customYearMonthTo)
      console.log(`📅 Custom date range: ${customYearMonthFrom} to ${customYearMonthTo}`)
    }

    const env = localStorage.getItem('env') as string
    const API_URL = getIntegrationURLFromENV(env)
    const API_URL_PLD = getIntegrationURLFromENVForPLD()

    let INTEGRATION_ENDPOINT = API_URL
      // refType === AlexiApplicationTypes.PARTNER_CONNECT_APPLICATION ? API_URL : API_URL_PLD

    try {
      const res = await axios.post<any>(
        INTEGRATION_ENDPOINT + 'ext/api/upload-process/statements',
        formData
      )
      console.log('✅ Response upload-process/statements: ', res)
      console.log('✅ Response data: ', res?.data)

      if (res.data) {
        const uploadedFiles = JSON.parse(window.localStorage.getItem('uploadedFiles') as string)
        window.localStorage.setItem('uploadedFiles', (uploadedFiles + 1).toString())
        
        console.log('🔄 Setting upload status to success...')
        setUploadStatus('success')
        
        console.log('✅ Standard mode: File validated by Perfios')
        console.log('✅ Calling handleUploadedFileRes with:', res.data)
        handleUploadedFileRes(res.data)
        console.log('✅ Upload complete, status should be success')
        /*
         * made the upload and process common in above step below code will be unused....
         */

        // const payload = {
        //   perfiosTransactionId: transactionId.perfiosTransactionId,
        //   files: [res.data?.Location],
        //   validateMonthsTill: validateMonthsTill,
        // }
        // setData((prev: any) => {
        //   console.log(prev,109)
        //   return [...prev, payload]
        // })

        // const ProcessRes = await axios.post<UploadedBankStmtRes>(
        //   INTEGRATION_ENDPOINT + 'ext/api/process/statements',
        //   payload
        // )
        // if (ProcessRes.data) {
        //   const uploadedFiles = JSON.parse(
        //     window.localStorage.getItem('uploadedFiles') as string
        //   )
        //   window.localStorage.setItem('uploadedFiles', (uploadedFiles + 1).toString())
        //   setUploadStatus('success')
        //   // setFileResponseStatus(prev => [...prev, { fileDetails: file, status: 400, error: "internal server error" }]);

        //   handleUploadedFileRes(ProcessRes.data)
        // }
      }
    } catch (error: any) {
      setUploadStatus('error')
      setSkipDetails(true)
      handleFailedFiles(file, 400, error?.response?.data?.message, fileUploadResponse)
      if (error?.response?.data === undefined) {
        setErrorMsg('Please upload failed bank statement again')
      } else if (
        error?.response?.data?.message === undefined ||
        error?.response?.data?.message.toLowerCase().includes('internal server error') ||
        error?.response?.data?.message.toLowerCase().includes('internal error')
      ) {
        const accountDetails = JSON.parse(
          window.localStorage.getItem('accountDetails') as string
        ) as Banks[]
        const completedAccounts = accountDetails.filter((ac: Banks) => ac.completed === true)
        if (accountDetails.length === completedAccounts.length) {
          setUploadStatus('success')
          setErrorMsg('')
        } else {
          // setFileResponseStatus(prev => [...prev, file]);
          setErrorMsg(
            `Please try reuploading this statement. If issue persists, reach out at ${`loans@credilinq.ai`}`
          )
        }
      } else if (
        error?.response?.data?.message
          .toLowerCase()
          .includes('an active transaction corresponding to this transaction id') ||
        error?.response?.data?.message.toLowerCase().includes('transaction id')
      ) {
        setErrorMsg(
          `Please try reuploading this statement. If issue persists, reach out at ${`loans@credilinq.ai`}`
        )
      } else {
        setErrorMsg(error?.response?.data?.message)
      }

      await sendErrorLog(
        (file as any)?.path,
        refId as string,
        refType as string,
        error,
        ErrorLogUploadType.STATEMENT_UPLOAD
      )
    }
  }

  const getIcon = () => {
    if (uploadStatus === 'error') {
      return <ArrowClockwise className='text-danger' size={20} />
    }
    if (uploadStatus === 'loading') {
      // return <FileEarmarkArrowUpFill size={20} />
      return (
        <img src={toAbsoluteUrl('/media/loaders/loader.gif')} alt='img' width={20} height={20} />
      )
    }
    return <span></span>
  }

  const getCancelIcon = () => {
    if (errorMsg) {
      return <Xicon onClick={onClose} style={{ cursor: 'pointer' }} />
    } else {
      return <Xicon onClick={onClose} style={{ cursor: 'pointer' }} />
    }
  }
  return !skipDetails ? (
    <>
      <Stack>
        <Stack
          className='capsuleText'
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px', // Adjusted gap for better spacing
            color: '#616B75',
            background: '#F9FAFA',
            borderRadius: 4,
            padding: '8px 16px',
            fontSize: '12px',
            minWidth: '100%',
            display: 'flex', // Changed to 'flex' for better alignment
          }}
          title={file?.name}
        >
          {getIcon()}
          <div
            style={{
              fontSize: 14,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexGrow: 1, // Allow this div to grow and take available space
            }}
          >
            {file?.name}
          </div>
          {uploadStatus !== 'loading' && getCancelIcon()}
          {getUploadStatus(uploadStatus)}
        </Stack>
        {uploadStatus === 'loading' && (
          <>
            {getUploadStatus(uploadStatus)}
            <div style={{ fontSize: 10, color: 'gray', marginTop: '4px' }}>
              <span style={{ fontSize: 14 }}>
                Please wait while your statements are being uploaded.
              </span>
            </div>
          </>
        )}
        {errorMsg && (
          <div className='text-danger' style={{ fontSize: 14, marginTop: '4px' }}>
            <span style={{ fontSize: 14 }}>*</span>
            {errorMsg}
          </div>
        )}
      </Stack>
    </>
  ) : null
}

export default UploadBankStatement
